import { AppState } from "@Store/index";
import { sortedConfigsAccordingToPrice } from "@Utils/generateRoomTypeSchemaData";
import { PropsWithChildren, createContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "hooks/useHistory";
import useWhitelabel from "hooks/useWhitelabel";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import TITLE_MAP from "../data/titleMap";
import fetchInventoryRooms from "@Pages/PropertyPage/data/fetchInventoryRooms";
import { getFiltersFromQuery, getParams } from "@Pages/PropertyPage/data/helper";
import {
  updatePropertyPageHref,
  updateRoomTypes,
} from "@Pages/PropertyPage/data/propertyPageReducer";

export type RoomTypeContextType = {
  isHMO?: boolean;
  isPartnered?: boolean;
  propertyData?: any;
  roomTypeCount?: number;
  roomTypes?: any;
  unitType?: string;
  isLoading?: boolean;
  isLoadingForFilter?: boolean;
  showPrevDataWithoutShimmer?: boolean;
};

type TitleMapType = keyof typeof TITLE_MAP;

export const RoomTypeContext = createContext<RoomTypeContextType>({});

// Module-level tracker to prevent duplicate room_types API calls across remounts
let activeFetchKey: string | null = null;

const RoomTypeContextProvider = ({
  children,
  isLoading,
}: PropsWithChildren<{ isLoading?: boolean }>) => {
  const {
    data: propertyData,
    roomTypes,
    propertyPageHref,
    canonicalName,
    userLanguage,
    userCurrency,
  } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      data: propertyPageData.data,
      canonicalName: propertyPageData.canonical_name,
      roomTypes: propertyPageData.roomTypes,
      propertyPageHref: propertyPageData.href,
      userLanguage: appData.userLanguage,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  // Strip `#room-<id>` / `#room_type` once it has scrolled, for every caller and both
  // room trees. After CustomAccordianItem's 500ms hash scroll. Not on whitelabel, which has
  // no room gallery and keeps its URLs as they were.
  const { hash } = useLocation();
  const history = useHistory();
  const { isWhitelabel } = useWhitelabel();
  useEffect(() => {
    if (isWhitelabel) return undefined;
    if (!hash || !(hash.startsWith("#room-") || hash === "#room_type")) return undefined;
    const timerId = setTimeout(() => {
      // Keep the entry's state: a modal opened meanwhile owns it (`_modals`). Read live —
      // `history.location` here is the render-time snapshot.
      history.replace(
        `${window.location.pathname}${window.location.search}`,
        window.history.state?.state,
      );
    }, 1200);
    return () => clearTimeout(timerId);
  }, [hash]);

  const roomTypesWithSortedData = {
    ...roomTypes,
    data: sortedConfigsAccordingToPrice(roomTypes.data || []),
  };

  const unitType =
    TITLE_MAP[String(propertyData?.meta?.unit_type) as TitleMapType] ?? "Configurations";

  const isPartnered = Boolean(
    propertyData?.meta?.is_partnered ?? propertyData?.meta_short?.is_partnered,
  );

  const isHMO = useMemo(() => {
    const { unit_type: unitTypeHMO, inventory_type: inventoryType } = propertyData?.meta || {};
    return unitTypeHMO === "independent_house" && inventoryType === "entire_place";
  }, [propertyData.meta]);

  useEffect(() => {
    if (isLoading || !canonicalName || canonicalName.length === 0) return;

    const { href } = window.location;
    const fetchKey = `${canonicalName}__${href}`;

    // Skip if this exact fetch is already in-flight or completed
    if (activeFetchKey === fetchKey) return;

    // Skip if roomTypes already has matching data
    if (
      roomTypes.id &&
      propertyData.canonical_name === roomTypes.id &&
      propertyPageHref === href &&
      roomTypesWithSortedData?.data?.length
    ) {
      return;
    }

    activeFetchKey = fetchKey;
    dispatch(updatePropertyPageHref(href));

    const filters = getFiltersFromQuery(href) || {};
    const filterParams = getParams(filters);

    const getRoomTypeData = async () => {
      const res = await fetchInventoryRooms({
        id: propertyData.canonical_name,
        params: { ...filterParams, user_currency: userCurrency },
        selectedLang: userLanguage,
      });
      const data = res?.data?.data?.result || [];
      const count = res?.data?.data?.result?.length || 0;
      if (res === null) {
        dispatch(
          updateRoomTypes({
            isLoading: true,
            showPrevDataWithoutShimmer: true,
            data: roomTypes?.data || [],
            count: roomTypes?.data?.length || 0,
          }),
        );
      } else {
        dispatch(
          updateRoomTypes({ isLoading: false, data, count, id: propertyData.canonical_name }),
        );
      }
    };

    getRoomTypeData();
  }, [canonicalName, isLoading]);

  return (
    <RoomTypeContext.Provider
      value={{
        isLoading: roomTypes.isLoading,
        isLoadingForFilter: roomTypes.isLoadingForFilter,
        isHMO,
        propertyData,
        roomTypes: roomTypesWithSortedData,
        unitType,
        isPartnered,
        showPrevDataWithoutShimmer: roomTypes.showPrevDataWithoutShimmer,
      }}
    >
      {children}
    </RoomTypeContext.Provider>
  );
};

export default RoomTypeContextProvider;
